import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const CartPage: React.FC = () => {
  const { cartItems, cartTotal, removeFromCart, updateQuantity, loading } = useCart();
  const { user, openLogin } = useAuth();
  const navigate = useNavigate();

  const SKIP_LENS_CATEGORIES = [3, 4, 5]; // Sunglasses, Contact Lenses, Blue Light
  const needsLensSelection = cartItems.some(
    (item) => !SKIP_LENS_CATEGORIES.includes(item.category_id ?? 0)
  );

  const handleCheckout = () => {
    if (needsLensSelection) {
      navigate('/check');
    } else {
      localStorage.removeItem('lensSelectionData');
      navigate('/address');
    }
  };

  if (!user) {
    return (
      <div className="cart-page">
        <div className="empty-state">
          <div className="empty-state__icon">🛒</div>
          <h2>Your cart is waiting</h2>
          <p>Sign in to view your cart and continue shopping.</p>
          <button className="btn btn--primary" onClick={() => openLogin()}>Sign In</button>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="empty-state">
          <div className="empty-state__icon">🛒</div>
          <h2>Your cart is empty</h2>
          <p>Add some eyewear to get started!</p>
          <Link to="/products" className="btn btn--primary">Shop Now</Link>
        </div>
      </div>
    );
  }

  // const shipping = cartTotal >= 500 ? 0 : 99;
  const grandTotal = cartTotal;

  return (
    <div className="cart-page">
      <div className="breadcrumb">
        <Link to="/">Home</Link>
        <span className="breadcrumb__sep">›</span>
        <span>Your Cart</span>
      </div>

      <h1 className="cart-page__title">Your Cart <span className="cart-page__count">{cartItems.length} items</span></h1>

      <div className="cart-layout">
        {/* Cart items */}
        <div className="cart-items">
          <div className="cart-items__header">
            <span>Product</span>
            <span>Price</span>
            <span>Quantity</span>
            <span>Total</span>
            <span></span>
          </div>

          {cartItems.map((item) => {
            const image = item.image_uri?.[0] || '';
            const itemTotal = item.selling_price * item.quantity;
            return (
              <div key={item.id} className="cart-item">
                <div className="cart-item__product">
                  <img
                    src={image}
                    alt={item.product_name}
                    className="cart-item__image"
                    onClick={() => navigate(`/product/${item.product_id}`)}
                  />
                  <div className="cart-item__details">
                    <h4 className="cart-item__name" onClick={() => navigate(`/product/${item.product_id}`)}>
                      {item.product_name}
                    </h4>
                    {item.brand && <p className="cart-item__brand">{item.brand}</p>}
                    {item.size && <p className="cart-item__meta">{item.size}</p>}
                  </div>
                </div>

                <span className="cart-item__price">₹{Math.round(item.selling_price).toLocaleString()}</span>

                <div className="quantity-stepper quantity-stepper--sm">
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                  >−</button>
                  <span>{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    disabled={item.quantity >= (item.available_quantity || 99)}
                  >+</button>
                </div>

                <span className="cart-item__total">₹{Math.round(itemTotal).toLocaleString()}</span>

                <button
                  className="cart-item__remove"
                  onClick={() => removeFromCart(item.id)}
                  title="Remove"
                >
                  🗑
                </button>
              </div>
            );
          })}

          <div className="cart-items__footer">
            <Link to="/products" className="continue-shopping">
              ← Continue Shopping
            </Link>
          </div>
        </div>

        {/* Order summary */}
        <div className="order-summary">
          <h3 className="order-summary__title">Order Summary</h3>

          <div className="order-summary__rows">
            <div className="order-summary__row">
              <span>Subtotal</span>
              <span>₹{Math.round(cartTotal).toLocaleString()}</span>
            </div>
            {/* <div className="order-summary__row">
              <span>Shipping</span>
              <span>{shipping === 0 ? 'Free' : `₹${shipping}`}</span>
            </div> */}
          </div>

          <div className="order-summary__total">
            <span>Total</span>
            <span>₹{Math.round(grandTotal).toLocaleString()}</span>
          </div>

          {/* {shipping > 0 && (
            <p className="order-summary__shipping-note">
              Add ₹{Math.round(500 - cartTotal)} more for free shipping
            </p>
          )} */}

          <button
            className="btn btn--primary order-summary__checkout-btn"
            onClick={handleCheckout}
          >
            Proceed to Checkout
          </button>

          <div className="order-summary__accepts">
            {/* <p>We accept</p> */}
            <div className="order-summary__payments">
              {['VISA', 'MC', 'UPI', 'PayPal'].map((p) => (
                <span key={p} className="footer__payment-badge">{p}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
